import { ExpandLessOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useId, useState } from 'react';
import type { NavigationModeAction, SectionNavigationGroup } from './navigation';
import { designTokens } from './tokens';
import { createFocusRingStyles } from './interaction';

export const sectionSidebarWidth = designTokens.layout.sectionSidebarWidth;

const navigationItemStyle = {
  minHeight: designTokens.layout.sectionSidebarItemHeight,
  [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize },
  pl: `${designTokens.layout.menuItemInlineInset}px`,
  pr: `${designTokens.layout.menuItemEndInset}px`,
  py: `${designTokens.layout.menuItemBlockInset}px`,
  gap: `${designTokens.layout.menuItemGap}px`,
  borderRadius: designTokens.radius.navigation,
  color: designTokens.navigation.text,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: designTokens.navigation.hoverSurface,
    color: designTokens.navigation.hoverText,
    '& .MuiListItemIcon-root, & > svg': { color: 'inherit' },
  },
  '&.Mui-focusVisible, &:focus-visible': createFocusRingStyles({ inset: true, color: designTokens.navigation.focus }),
} as const;

const navigationItemIconStyle = {
  minWidth: designTokens.layout.sectionSidebarIconSize,
  color: designTokens.navigation.mutedText,
  '& svg': { fontSize: designTokens.layout.sectionSidebarIconSize },
} as const;

const navigationItemTextStyle = {
  ...designTokens.typography.ui,
  fontWeight: 500,
  letterSpacing: '-0.015em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

export function SectionSidebar<PageID extends string>({ ariaLabel, groups, currentPage, onNavigate, mode = 'employee', footerAction, compact = false }: { ariaLabel: string; groups: readonly SectionNavigationGroup<PageID>[]; currentPage: PageID; onNavigate: (page: PageID) => void; mode?: 'employee' | 'admin'; footerAction?: NavigationModeAction; compact?: boolean }) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const sidebarID = useId().replaceAll(':', '');
  const activeGroup = groups.find((group) => group.items.some((item) => item.id === currentPage));
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(() => new Set(activeGroup?.collapsible ? [activeGroup.id] : []));

  useEffect(() => {
    if (!activeGroup?.collapsible) return;
    setExpandedGroups((current) => current.has(activeGroup.id) ? current : new Set([...current, activeGroup.id]));
  }, [activeGroup?.collapsible, activeGroup?.id]);

  const toggleGroup = (groupID: string) => setExpandedGroups((current) => {
    const next = new Set(current);
    if (next.has(groupID)) next.delete(groupID);
    else next.add(groupID);
    return next;
  });

  return <Box component="nav" aria-label={ariaLabel} data-testid="section-sidebar" sx={{ width: compact ? 'auto' : sectionSidebarWidth, flex: compact ? 1 : '0 0 auto', minWidth: 0, height: '100%', borderRight: '1px solid', borderColor: designTokens.navigation.divider, bgcolor: designTokens.navigation.surface, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <Box data-testid="section-sidebar-menu" sx={{ flex: 1, minHeight: 0, overflowX: 'hidden', overflowY: 'auto', overscrollBehaviorY: 'contain', scrollbarWidth: 'thin', scrollbarColor: `${designTokens.navigation.divider} transparent`, px: 1.5, pt: 1.5, pb: 1.5 }}>
      {groups.map((group, groupIndex) => {
        const expanded = !group.collapsible || expandedGroups.has(group.id);
        const groupTriggerID = `${sidebarID}-group-${groupIndex}-trigger`;
        const groupPanelID = `${sidebarID}-group-${groupIndex}-items`;
        return <Box key={group.id} sx={{ mb: groupIndex === groups.length - 1 ? 0 : mode === 'admin' ? `${designTokens.layout.sectionSidebarItemGap}px` : 1.75 }}>
        {group.label && !group.collapsible && <Typography component="h2" sx={{ px: 1.5, pt: 0.75, pb: 0.6, color: designTokens.navigation.mutedText, ...designTokens.typography.supporting, fontWeight: 650, letterSpacing: '-0.01em' }}>{group.label}</Typography>}
        {group.label && group.collapsible && <ListItemButton id={groupTriggerID} aria-controls={groupPanelID} aria-expanded={expanded} onClick={() => toggleGroup(group.id)} sx={navigationItemStyle}>
          <ListItemIcon sx={navigationItemIconStyle}>{group.icon}</ListItemIcon>
          <ListItemText sx={{ m: 0, minWidth: 0 }} primary={group.label} slotProps={{ primary: { sx: navigationItemTextStyle } }} />
          {expanded ? <ExpandLessOutlined sx={{ fontSize: designTokens.layout.sectionSidebarIconSize, color: designTokens.navigation.mutedText }} /> : <ExpandMoreOutlined sx={{ fontSize: designTokens.layout.sectionSidebarIconSize, color: designTokens.navigation.mutedText }} />}
        </ListItemButton>}
        <Collapse id={groupPanelID} role={group.collapsible ? 'group' : undefined} aria-labelledby={group.collapsible ? groupTriggerID : undefined} in={expanded} timeout={reducedMotion ? 0 : designTokens.motion.expand.duration} easing={designTokens.motion.expand.easing} unmountOnExit={false}>
          <List disablePadding dense sx={{ display: 'flex', flexDirection: 'column', gap: `${designTokens.layout.sectionSidebarItemGap}px`, pt: group.collapsible ? `${designTokens.layout.sectionSidebarItemGap}px` : 0 }}>
          {group.items.map((item) => {
            const selected = item.id === currentPage;
            const nested = Boolean(group.collapsible);
            return <ListItemButton key={item.id} aria-current={selected ? 'page' : undefined} selected={selected} onClick={() => onNavigate(item.id)} sx={{
              ...navigationItemStyle,
              pl: `${designTokens.layout.menuItemInlineInset + (nested ? designTokens.layout.sectionSidebarIconSize + designTokens.layout.menuItemGap : 0)}px`,
              '&.Mui-selected, &.Mui-selected.Mui-focusVisible, &.Mui-selected:focus-visible': { bgcolor: designTokens.navigation.selectedSurface, color: designTokens.navigation.selectedText, '& .MuiListItemIcon-root': { color: designTokens.navigation.selectedIcon } },
              '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible:hover, &.Mui-selected:focus-visible:hover': { bgcolor: designTokens.navigation.selectedHoverSurface, color: designTokens.navigation.selectedHoverText, '& .MuiListItemIcon-root': { color: designTokens.navigation.selectedHoverIcon } },
            }}>
              {!nested && <ListItemIcon sx={{ ...navigationItemIconStyle, color: selected ? designTokens.navigation.selectedIcon : designTokens.navigation.mutedText }}>{item.icon}</ListItemIcon>}
              <ListItemText sx={{ m: 0, minWidth: 0 }} primary={item.label} slotProps={{ primary: { sx: { ...navigationItemTextStyle, fontWeight: selected ? 600 : 500 } } }} />
              {item.badge !== undefined && <Typography sx={{ minWidth: 20, height: 20, px: 0.5, borderRadius: designTokens.radius.pill, display: 'grid', placeItems: 'center', bgcolor: selected ? designTokens.navigation.selectedBadgeSurface : designTokens.navigation.badgeSurface, color: selected ? designTokens.navigation.selectedBadgeText : designTokens.navigation.badgeText, ...designTokens.typography.supporting, fontWeight: 700 }}>{item.badge}</Typography>}
            </ListItemButton>;
          })}
          </List>
        </Collapse>
      </Box>;})}
    </Box>
    {footerAction && <Box sx={{ flexShrink: 0, p: 1.5, borderTop: '1px solid', borderColor: designTokens.navigation.divider }}>
      <ListItemButton data-testid="navigation-mode-action" onClick={footerAction.onSelect} sx={navigationItemStyle}>
        <ListItemIcon sx={navigationItemIconStyle}>{footerAction.icon}</ListItemIcon>
        <ListItemText sx={{ m: 0, minWidth: 0 }} primary={footerAction.label} slotProps={{ primary: { sx: navigationItemTextStyle } }} />
      </ListItemButton>
    </Box>}
  </Box>;
}
